import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, BookOpen, ExternalLink, Cpu, FileText } from 'lucide-react';
import { api } from '../lib/api';
import SEO from '../components/SEO';

const popularSearches = [
  'Arduino Uno schematic',
  'L298N motor driver pinout',
  'ESP32 pinout diagram',
  'LM7805 voltage regulator circuit',
  '555 timer astable circuit',
  'H-bridge motor driver schematic',
  'Raspberry Pi GPIO pinout',
  'MOSFET switch circuit',
];

export default function Schematics({ isEmbedded = false }: { isEmbedded?: boolean }) {
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    const initial = location.state?.initialQuery;
    if (initial) {
      setQuery(initial);
      handleSearch(initial);
    }
  }, [location.state]);

  const handleSearch = async (searchQuery: string = query) => {
    const q = searchQuery.trim();
    if (!q) return;
    setLoading(true);
    setSearched(true);
    try {
      const data = await api.searchDiagrams(q);
      setResults(data?.results || data?.diagrams || []);
    } catch (e) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: isEmbedded ? 'auto' : 'calc(100vh - 64px)', 
      padding: isEmbedded ? '1rem' : '0 1.5rem 3rem',
      overflowY: isEmbedded ? 'auto' : 'visible'
    }}>
      {!isEmbedded && (
        <SEO 
          title="Circuit Diagrams & Schematics | DIYElectronicMech" 
          description="Search for electronics schematics, circuit diagrams, and datasheets. Generate AI-powered circuit designs for Arduino, ESP32, and custom PCB projects."
        />
      )}
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

        {/* Page header */}
        <div style={{ padding: isEmbedded ? '1rem 0' : '2.5rem 0 2rem', textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.75rem' }}>
            <div style={{
              width: '52px', height: '52px',
              background: 'rgba(255,170,0,0.1)',
              border: '1px solid rgba(255,170,0,0.3)',
              borderRadius: '14px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <BookOpen size={24} color="var(--color-accent)" />
            </div>
          </div>
          <h1 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '0.5rem' }}>
            Circuit <span style={{ color: 'var(--color-accent)' }}>Schematics</span>
          </h1>
          <p style={{ color: 'var(--text-muted)', maxWidth: '480px', margin: '0 auto', fontSize: '0.9rem' }}>
            Search for circuit diagrams, pinout references, datasheets, and technical documentation.
          </p>
        </div>

        {/* Search bar */}
        <div style={{
          background: 'var(--bg-surface)',
          border: '1px solid rgba(255,170,0,0.2)',
          borderRadius: '16px',
          padding: '1.25rem',
          marginBottom: '1.5rem',
          boxShadow: '0 0 20px rgba(255,170,0,0.04)',
        }}>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input
                autoFocus
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder="Search: Arduino Uno schematic, 555 timer circuit, L298N pinout..."
                style={{
                  width: '100%',
                  background: 'var(--bg-surface-2)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '10px',
                  padding: '0.75rem 1rem 0.75rem 2.5rem',
                  color: 'var(--text-primary)',
                  fontSize: '0.9rem',
                  outline: 'none',
                  fontFamily: 'var(--font-body)',
                  transition: 'border-color 0.2s ease',
                }}
                onFocus={e => { e.target.style.borderColor = 'rgba(255,170,0,0.4)'; }}
                onBlur={e => { e.target.style.borderColor = 'var(--border-color)'; }}
              />
            </div>
            <button
              onClick={() => handleSearch()}
              disabled={loading || !query.trim()}
              style={{
                background: 'linear-gradient(135deg, #ffaa00, #cc8800)',
                color: '#050912',
                border: 'none',
                borderRadius: '10px',
                padding: '0.75rem 1.5rem',
                fontWeight: 700,
                fontSize: '0.9rem',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                opacity: !query.trim() || loading ? 0.5 : 1,
                transition: 'all 0.2s ease',
              }}
            >
              {loading ? '...' : <><Search size={15} /> Search</>}
            </button>
          </div>
        </div>

        {/* Results */}
        {searched && (
          <div style={{ marginBottom: '2rem' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                <Cpu size={32} style={{ animation: 'spin-slow 2s linear infinite', marginBottom: '0.75rem', color: 'var(--color-accent)' }} />
                <p>Searching schematics & datasheets...</p>
              </div>
            ) : results.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {results.map((r: any, i: number) => (
                  <div key={i} style={{
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '12px',
                    padding: '1.25rem',
                    display: 'flex', flexDirection: 'column', gap: '1rem',
                    transition: 'border-color 0.2s ease',
                  }}
                    onMouseEnter={e => (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,170,0,0.3)'}
                    onMouseLeave={e => (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-color)'}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                        <div style={{
                          width: '36px', height: '36px', flexShrink: 0,
                          background: 'rgba(255,170,0,0.1)', border: '1px solid rgba(255,170,0,0.2)',
                          borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <FileText size={16} color="var(--color-accent)" />
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, marginBottom: '0.2rem', color: 'var(--text-primary)' }}>{r.title || r.name}</div>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{r.description || r.source}</div>
                          {r.url && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.2rem', fontFamily: 'var(--font-mono)' }}>{r.url}</div>}
                        </div>
                      </div>
                      {r.url && (
                        <a href={r.url} target="_blank" rel="noopener noreferrer"
                          style={{ color: 'var(--color-accent)', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.3rem', textDecoration: 'none', flexShrink: 0 }}>
                          Open <ExternalLink size={13} />
                        </a>
                      )}
                    </div>
                    {r.url && (
                      <div style={{ marginTop: '0.5rem', width: '100%', display: 'flex', justifyContent: 'center', background: 'rgba(0,0,0,0.2)', padding: '1rem', borderRadius: '8px' }}>
                        <img src={r.url} alt={r.title || 'Diagram'} style={{ maxWidth: '100%', maxHeight: '400px', objectFit: 'contain', borderRadius: '4px' }} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                <p>No schematics found. Try a different search.</p>
              </div>
            )}
          </div>
        )}

        {/* Popular searches */}
        {!searched && (
          <div>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>
              POPULAR SEARCHES:
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {popularSearches.map((s) => (
                <button
                  key={s}
                  onClick={() => { setQuery(s); handleSearch(s); }}
                  style={{
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    padding: '0.45rem 0.875rem',
                    color: 'var(--text-secondary)',
                    fontSize: '0.82rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontFamily: 'var(--font-body)',
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,170,0,0.35)';
                    (e.currentTarget as HTMLElement).style.color = 'var(--color-accent)';
                    (e.currentTarget as HTMLElement).style.background = 'rgba(255,170,0,0.06)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-color)';
                    (e.currentTarget as HTMLElement).style.color = 'var(--text-secondary)';
                    (e.currentTarget as HTMLElement).style.background = 'var(--bg-surface)';
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
